import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { SKIP_GLOBAL_ERROR_TOAST } from '../../interceptors/token/token.interceptor';
import {
  ConflictLogPayload,
  ValidationCacheItem,
} from '../offline-validation/types';

export interface TicketValidationData {
  porter_code: number;
  ticket_code: string;
  all_marked: boolean;
}

export interface TicketInfo {
  ticket: {
    id: number;
    code: string;
    userName: string;
    userEmail: string;
    isUsed: number;
    statusId?: number; // ACC-013 — 2 = cancelado/suspendido
    statusName?: string;
    functionStartDate: string;
    functionName: string;
    venueName: string;
    zoneName: string;
    seatId: string;
    experienceName: string;
    mediaExperienceUrl?: string;
    mediaUrl?: string;
  };
  relatedTicket?: any[];
  function: {
    port_code: number;
    function_id: number;
  };
}

export interface TicketValidationResponse {
  ticket: {
    id: number;
    code: string;
    userName: string;
    userEmail: string;
    isUsed: number;
    statusId?: number; // ACC-013 — 2 = cancelado/suspendido
    statusName?: string;
    functionStartDate: string;
    functionName: string;
    venueName: string;
    zoneName: string;
    seatId: string;
    experienceName: string;
    mediaExperienceUrl?: string;
    mediaUrl?: string;
  };
  relatedTicket?: any[];
  function: {
    port_code: number;
    function_id: number;
  };
}

export interface ExitControlData {
  ticket_code: string;
  porter_code: number;
}

/** Una fila del recuento de accesos por zona del pie de la vista de puerta. */
export interface ZoneUsageRow {
  zone_id: number;
  zone_name: string;
  /** Boletos que entraron AL MENOS UNA VEZ (`is_used = 1`), no "adentro ahora". */
  utilizados: number;
  vendidos: number;
}

export interface ZoneUsageResponse {
  data: {
    /** `false` cuando la experiencia tiene el recuento apagado; `zones` viene vacío. */
    enabled: boolean;
    function_id?: number;
    zones: ZoneUsageRow[];
  };
}

export interface ExitControlResponse {
  data: {
    id: number;
    ticket_code: string;
    porter_code: number;
    is_in_event: number;
    is_out_event: number;
    created_at: string;
    updated_at: string;
  }[];
}

@Injectable({
  providedIn: 'root'
})
export class SaleValidateService {

  constructor(private readonly http: HttpClient, @Inject('environment') private readonly env: any) { }
  
  /**
   * Obtiene información completa del ticket (público)
   * @param code Código del ticket
   * @returns Observable con la información del ticket
   */
  getTicketInfo(code: string): Observable<TicketValidationResponse> {
    return this.http.get<TicketValidationResponse>(`${this.env.apiBase}experience/ticket/info/${code}`);
  }

  /**
   * Valida un ticket (requiere autenticación)
   * @param code Código del ticket
   * @returns Observable con la validación del ticket
   */
  validateCode(code: string): Observable<any> {
    return this.http.get(`${this.env.apiBase}experience/ticket/validate-ticket/${code}`);
  }

  /**
   * Recuento de accesos por zona (utilizados / vendidos) de la función del
   * boleto. Corre con `VALIDAR_BOLETO_QR_EXP`, el permiso que ya tiene la
   * pantalla de puerta — no con el del reporte del productor.
   *
   * El backend aplica el flag `is_access_counter_visible` de la experiencia: si
   * está apagado devuelve `enabled: false` y `zones: []`.
   */
  getZoneUsage(code: string): Observable<ZoneUsageResponse> {
    return this.http.get<ZoneUsageResponse>(
      `${this.env.apiBase}experience/ticket/zone-usage/${code}`
    );
  }

  /**
   * Marca un ticket como usado.
   *
   * C2-FRO-002 Fase 3: acepta `idempotencyKey` opcional. Cuando el cliente
   * hace replay de validaciones offline, debe mandar el mismo UUID v4 en
   * cada retry — eso permite que C2-SEC-002 server-side deduplique
   * (UPDATE atómico WHERE is_used=0 RETURNING id + UNIQUE en exit_control).
   *
   * Sin el header, el comportamiento es idéntico al previo (sin breaking
   * change para callers existentes del flow online puro).
   *
   * @param data Datos de validación
   * @param idempotencyKey UUID v4 generado al enqueue offline; mismo entre retries
   * @returns Observable con el resultado de la validación
   */
  markTicketInUse(
    data: TicketValidationData,
    idempotencyKey?: string,
  ): Observable<any> {
    // El header estándar es `Idempotency-Key` (sin prefijo `X-`, deprecado por
    // RFC 6648). Debe coincidir EXACTO con el `allowedHeaders` del enrutador
    // (apps/router/server.js) y de core (apps/core/src/main.ts); si no, el
    // preflight CORS del PUT falla y el browser bloquea la request. Es además el
    // nombre que usan el resto de services (partners, wallets).
    // SKIP_GLOBAL_ERROR_TOAST: el componente de validación ya muestra un toast con
    // el mensaje correcto ("Código de puerta inválido", etc.). Sin esto, el
    // interceptor global mostraba un SEGUNDO toast genérico ("Función incorrecta").
    const options: { headers?: HttpHeaders; context: HttpContext } = {
      context: new HttpContext().set(SKIP_GLOBAL_ERROR_TOAST, true),
    };
    if (idempotencyKey) {
      options.headers = new HttpHeaders({ 'Idempotency-Key': idempotencyKey });
    }
    return this.http.put(
      `${this.env.apiBase}experience/ticket/validate-ticket`,
      data,
      options,
    );
  }

  // ============================================================
  // C2-FRO-002 Fase 1 backend integration — Offline validation
  // ============================================================

  /**
   * Pre-fetch del snapshot ligero de tickets activos para validación offline.
   * Llamado al iniciar turno desde validate.component.
   *
   * Backend: GET /api/experience/function/:id/validation-cache (Fase 1, sin PII).
   * Permisos: `VALIDAR_BOLETO_QR_EXP` + ownership via `fn-param:id`.
   */
  getValidationCache(functionId: number): Observable<{
    statusCode: number;
    data: {
      function_id: number;
      generated_at: string;
      total: number;
      tickets: ValidationCacheItem[];
    };
  }> {
    return this.http.get<any>(
      `${this.env.apiBase}experience/function/${functionId}/validation-cache`,
    );
  }

  /**
   * Registra un conflicto detectado al replay del sync offline.
   * Llamado por SyncOrchestrator cuando markTicketInUse devuelve 409.
   *
   * Backend: POST /api/experience/ticket/conflict-log (Fase 1, append-only).
   */
  logConflict(payload: ConflictLogPayload): Observable<any> {
    return this.http.post(
      `${this.env.apiBase}experience/ticket/conflict-log`,
      payload,
    );
  }

  /**
   * Listado paginado de conflictos por función (admin para revisión
   * post-evento). No se usa en flow operativo, solo por admin UI futuro.
   */
  getValidationConflicts(
    functionId: number,
    take = 100,
    skip = 0,
    conflictType?: string,
  ): Observable<any> {
    let url = `${this.env.apiBase}experience/function/${functionId}/validation-conflicts?take=${take}&skip=${skip}`;
    if (conflictType) {
      url += `&conflict_type=${encodeURIComponent(conflictType)}`;
    }
    return this.http.get(url);
  }

  /**
   * Obtiene el estado de control de entrada/salida de un ticket
   * @param ticketCode Código del ticket
   * @returns Observable con el estado de control de entrada/salida
   */
  getExitControlStatus(ticketCode: string): Observable<ExitControlResponse> {
    return this.http.get<ExitControlResponse>(`${this.env.apiBase}experience/ticket/exit-control-status/${ticketCode}`);
  }

  /**
   * Marca entrada al evento
   * @param data Datos de control de entrada
   * @returns Observable con el resultado de la operación
   */
  markEntryToEvent(data: ExitControlData): Observable<any> {
    // El backend (RequestPutControlExitDto) espera `exitControl: 'in_event' | 'out_event'`
    // (ControlExitEnum), NO `is_in_event`/`is_out_event`. El servicio traduce ese enum a
    // los flags is_in_event/is_out_event al guardar el ExitControl.
    return this.http.put(`${this.env.apiBase}experience/ticket/control-exit/${data.ticket_code}`, {
      porter_code: data.porter_code,
      exitControl: 'in_event',
    }, { context: new HttpContext().set(SKIP_GLOBAL_ERROR_TOAST, true) });
  }

  /**
   * Marca salida del evento
   * @param data Datos de control de salida
   * @returns Observable con el resultado de la operación
   */
  markExitFromEvent(data: ExitControlData): Observable<any> {
    // Ver nota en markEntryToEvent: el backend espera `exitControl` (ControlExitEnum),
    // no los flags is_in_event/is_out_event.
    return this.http.put(`${this.env.apiBase}experience/ticket/control-exit/${data.ticket_code}`, {
      porter_code: data.porter_code,
      exitControl: 'out_event',
    }, { context: new HttpContext().set(SKIP_GLOBAL_ERROR_TOAST, true) });
  }
}
